/**
 * Parameter Panel Component
 * Implements PRD FR-1, FR-2, FR-3, FR-4
 */

import React from 'react';
import { useSimulationStore } from '../state/simulationStore';
import { ModelParams, PARAM_CONSTRAINTS } from '../model/types';
import { validateParam } from '../model/validation';

interface ParamInputProps {
  label: string;
  symbol: string;
  paramKey: keyof ModelParams;
  step?: number;
  description?: string;
}

function ParamInput({ label, symbol, paramKey, step = 1, description }: ParamInputProps) {
  const { params, setParam } = useSimulationStore();
  const value = params[paramKey];
  const constraint = PARAM_CONSTRAINTS[paramKey];
  const error = validateParam(paramKey, value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    if (!isNaN(newValue)) {
      setParam(paramKey, newValue);
    }
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} <span className="text-gray-500 font-mono">({symbol})</span>
      </label>
      <input
        type="number"
        value={value}
        onChange={handleChange}
        step={step}
        min={constraint.min}
        max={constraint.max}
        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {description && !error && (
        <p className="mt-1 text-xs text-gray-500">{description}</p>
      )}
    </div>
  );
}

export function ParameterPanel() {
  const { showAdvanced, toggleAdvanced, resetParamsToDefault } = useSimulationStore();

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Model Parameters</h2>
        <button
          onClick={resetParamsToDefault}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Reset to Defaults
        </button>
      </div>

      {/* Core Parameters */}
      <div className="space-y-1">
        <ParamInput
          label="Number of Market Makers"
          symbol="n"
          paramKey="n"
          step={1}
          description="Count of identical market makers (integer ≥ 1)"
        />

        <ParamInput
          label="Order Imbalance"
          symbol="i"
          paramKey="i"
          step={1}
          description="LT1's initial endowment (positive = seller)"
        />

        <ParamInput
          label="Expected Terminal Value"
          symbol="μ"
          paramKey="mu"
          step={1}
          description="Unconditional mean of terminal value"
        />

        <ParamInput
          label="Volatility"
          symbol="σ"
          paramKey="sigma"
          step={0.1}
          description="Std dev of each shock (σ² = variance)"
        />

        <ParamInput
          label="Risk Aversion"
          symbol="a"
          paramKey="a"
          step={0.001}
          description="CARA coefficient"
        />

        <ParamInput
          label="Initial Cash"
          symbol="W₀"
          paramKey="W0"
          step={100}
          description="Starting cash per agent (display only)"
        />
      </div>

      {/* Advanced Toggle */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <button
          onClick={toggleAdvanced}
          className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-800"
        >
          <span className={`mr-2 transform transition-transform ${showAdvanced ? 'rotate-90' : ''}`}>
            ▶
          </span>
          Advanced Parameters
        </button>

        {showAdvanced && (
          <div className="mt-4 pl-4 border-l-2 border-gray-200">
            <ParamInput
              label="Trading Fee"
              symbol="η"
              paramKey="eta"
              step={0.01}
              description="Per-share transaction fee"
            />

            <ParamInput
              label="Participation Cost"
              symbol="c"
              paramKey="c"
              step={0.1}
              description="Fixed cost of market presence"
            />
          </div>
        )}
      </div>
    </div>
  );
}
